import styled from "@emotion/styled";
import type { FormikHelpers } from "formik";
import { useState } from "react";
import z from "zod";

import {
  Alert,
  Button,
  DestructiveButton,
  FormField,
  FormikForm,
  Icon,
  LandingPageFieldText,
  Loading,
  PrimaryButton,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { LayoutForm, LayoutGroup } from "../styled.tsx";

type FormValues = {
  apiKey: string | null;
};

const validationSchema = z.object({
  // currently keys start with "sk-" but I don't know if that's
  // specifically documented...
  // Set a minimim length so at least the masking code isn't confused.
  apiKey: z.string().min(10).nullable(),
});

type OpenAIFormProps = {
  maskedApiKey: string | null;
  apiKeyState?: "valid" | "invalid" | "not-set";
  setApiKey: (apiKey: string | null) => Promise<string | null>;
};

export const OpenAIForm = ({ maskedApiKey, apiKeyState, setApiKey }: OpenAIFormProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [changing, setChanging] = useState(false);

  const handleApiKeySubmit = async (newValues: FormValues, helpers: FormikHelpers<FormValues>) => {
    const maskedValue = await setApiKey(newValues.apiKey);
    // Formik will internally update the value to the actual API key, but we want it
    // to use the new masked value as the clean state.
    await helpers.setValues({ apiKey: maskedValue });
    setChanging(false);
  };

  const loaded = apiKeyState !== undefined;
  const existing = maskedApiKey !== null;
  const editing = loaded && (changing || !existing);

  return (
    <LayoutGroup>
      <FormikForm<FormValues>
        initialValues={{ apiKey: maskedApiKey ?? "" }}
        formatStrings={formatStrings}
        validationSchema={validationSchema}
        onSubmit={handleApiKeySubmit}
        onReset={() => setChanging(false)}
      >
        {({ dirty, setFieldValue, isValid, isSubmitting, handleSubmit }) => (
          <LayoutForm blurOnEnter={false} $gridColumns="600px">
            {!changing && apiKeyState === "invalid" && (
              <Alert color="error" icon={<Icon icon="exclamation-circle" size={14} />}>
                {formatMessage("apiKeyInvalid")}
              </Alert>
            )}
            {!changing && apiKeyState === "valid" && (
              <Alert color="success" icon={<Icon icon="check" size={14} />}>
                {formatMessage("apiKeyValid")}
              </Alert>
            )}
            <FormField<FormValues> name="apiKey">
              <InputRow>
                <LandingPageFieldText fullWidth label={formatMessage("apiKeyLabel")} disabled={!editing} />
              </InputRow>
            </FormField>

            <ButtonContainer>
              {(changing || dirty) && !existing && (
                <>
                  <Button type="reset" disabled={isSubmitting}>
                    {formatMessage("cancelLabel")}
                  </Button>
                  <PrimaryButton type="submit" disabled={!isValid || isSubmitting}>
                    {isSubmitting && <Loading size={14} inline />}
                    {formatMessage("saveLabel")}
                  </PrimaryButton>
                </>
              )}
              {!changing && !dirty && existing && (
                <DestructiveButton
                  type="button"
                  onClick={async () => {
                    // Go through formik so we get the isSubmitting state
                    // for free.
                    setChanging(true);
                    await setFieldValue("apiKey", null);
                    handleSubmit();
                  }}
                >
                  {formatMessage("clearLabel")}
                </DestructiveButton>
              )}
            </ButtonContainer>
            <a href="https://platform.openai.com/settings/organization/api-keys" target="_blank" rel="noreferrer">
              {formatMessage("apiKeyDocumentationLink")}
            </a>
          </LayoutForm>
        )}
      </FormikForm>
    </LayoutGroup>
  );
};

const InputRow = styled.div`
  display: flex;
  max-width: 600px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  max-width: 600px;
  justify-content: flex-end;
`;

const formatStrings = {
  apiKeyLabel: {
    en: "API Key",
  },
  apiKeyDocumentationLink: {
    en: "Create an OpenAI API key here.",
  },

  cancelLabel: {
    en: "Cancel",
  },
  saveLabel: {
    en: "Save",
  },
  clearLabel: {
    en: "Clear",
  },
  changeLabel: {
    en: "Change",
  },

  apiKeyInvalid: {
    en: "Invalid API key",
  },
  // note this doesn't mean it actually has access, just that it exists.
  apiKeyValid: {
    en: "API key is valid",
  },
} satisfies FormatMessages;
